import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import HttpManager from "../../../../services/HttpManager";
import { RootState } from "../../../../globals";
import { getAccessPermissions } from "../../../../common";
import { EnrollmentHistoryDTO } from "../../../../models/EnrollmentHistoryDTO";
import { EnrollmentDueDayDTO } from "../../../../models/EnrollmentDueDayDTO";
import { ReentryInscriptionDTO, TargetSchedule, UPDATE_TYPE, UpdateOptionsDTO } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import PromocionDirecta, { faltaPromo, promoParaEnviar, promoVacia } from "../../../../components/acuerdos/promocionDirecta";
import InscripcionReingreso, { promoDeReingreso } from "../../../../components/acuerdos/inscripcionReingreso";
import { RequestPromotionDTO } from "../../../../models/EnrollmentHistoryDTO";
import BuscadorHorarios, { DetalleHorario, HorarioFila, filaDeDestino } from "../../../../components/horarios/buscadorHorarios";
import S from "./strings";
import { ACADEMIC_APPLY_TYPES, CHIP_VENCIMIENTO, TabProps, nombreHorario, TYPE_LABELS, TYPE_ORDER, typeChips, dinero, enrollmentStatusLabel, errorText, needsSchedule, planLinea, sessionDelta, tipoDe } from "./helpers";
import { ConfirmarAplicar, Fallo, PersonaConfirmar, UpdateDetail } from "./piezas";
import { etiquetaHorario } from "../../../../utils/etiquetaHorario";
import EstadoCuentaModal from "../../../../components/estadoCuenta/estadoCuentaModal";
import { apiBase, useCapacidades } from "./apiBase";
import { useEnviarAprobacion, useTieneCaja } from "../../../../components/aprobaciones/enviarAprobacion";
import { EleccionRecorrer, RecorrerEleccion, useVistaRecorrer } from "./recorrerPagos";
import { RecorrerVistaDTO } from "./apiBase";
import T from "./textosBase";
import SugerenciasHorario from "../../../../components/horarios/sugerenciasHorario";
import { RegresoClasesOpciones, RegresoProgramado, EleccionRegreso, regresoListo, regresoParaEnviar } from "../../../../components/horarios/regresoClases";
import { SugerenciaHorario, SugerenciasDTO } from "../../../../components/horarios/sugerenciasApi";

/** Color del estatus de la matrícula: verde SOLO Activo; las flotantes (Aplazado, Sin Horario, Pausado) en oro; lo demás neutro. */
const pillEstatus = (statusId: number | null | undefined): string => statusId === 1 ? "ok" : statusId === 7 || statusId === 9 || statusId === 10 ? "warn" : "flat";

// Estatus desde los que se pide una actualización: Activo, Aplazado, Sin horario, Pausado (y Retirado para Reingreso).
const UPDATABLE_STATUSES = [1, 7, 9, 10, 6];
// Cambio de horario (5) y Error (3) no explican nada: la matrícula siguió en otra o nunca existió.
const STATUS_SIN_AVISO = [3, 5];

interface Encontrada { id: number; name: string; code: string; phone: string; }

interface Props extends TabProps { initialStudentId?: number; initialEnrollmentId?: number; }

/** Actualizar, Una Alumna: busca a la alumna, ve su matrícula y plan, elige qué cambia y el horario destino con el Buscador de Horarios. */
const UnaAlumna: React.FC<Props> = ({ campusId, countryId, canApplyNow, notify, changed, initialStudentId, initialEnrollmentId }) => {
	const http = HttpManager.getInstance();
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const puedeSolicitar: boolean = !!getAccessPermissions(currentUser, "enrollmentRequests")?.create;
	const [busy, setBusy] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");
	const [found, setFound] = useState<Encontrada[] | null>(null);
	const [studentId, setStudentId] = useState<number | null>(initialStudentId ?? null);
	const [history, setHistory] = useState<EnrollmentHistoryDTO[] | null>(null);
	// Matrículas del plantel que NO se actualizan aquí (No Comenzado, Graduada…): explican por qué no hay nada que actualizar.
	const [cerradas, setCerradas] = useState<EnrollmentHistoryDTO[]>([]);
	const [studentCode, setStudentCode] = useState<string>("");
	const [enrollmentId, setEnrollmentId] = useState<number | null>(initialEnrollmentId ?? null);
	const [options, setOptions] = useState<UpdateOptionsDTO | null>(null);
	const [attendance, setAttendance] = useState<number | null>(null);
	const [due, setDue] = useState<EnrollmentDueDayDTO | null>(null);
	const cap = useCapacidades(campusId);
	// Cambio de Vencimiento (1-nov): Caja del plantel también lo pide aquí y queda Por Aprobar para Supervisión de Caja (como en la ficha).
	const tieneCaja = useTieneCaja();
	const aprobacion = useEnviarAprobacion(campusId);
	const [falloHistorial, setFalloHistorial] = useState<string | null>(null);
	const [falloOpciones, setFalloOpciones] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	const [verCuenta, setVerCuenta] = useState<boolean>(false);
	// Matrículas vigentes de la alumna en OTRO plantel (enlace desde otro plantel): se dice dónde están.
	const [otrosPlanteles, setOtrosPlanteles] = useState<string[]>([]);
	const planteles: any[] = useSelector((state: RootState) => state.rootReducer.catalog.campuses) || [];

	const [typeId, setTypeId] = useState<number | null>(null);
	const [destino, setDestino] = useState<HorarioFila | null>(null);
	const [buscando, setBuscando] = useState<boolean>(true);
	const [toScheduleId, setToScheduleId] = useState<number | null>(null);
	const [toCourseId, setToCourseId] = useState<number | null>(null);
	const [lastSession, setLastSession] = useState<string>("");
	const [dueDayDraft, setDueDayDraft] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [createdId, setCreatedId] = useState<number | null>(null);
	const [promo, setPromo] = useState<RequestPromotionDTO>(promoVacia());
	// Reingreso: inscripción nueva a precio de lista del horario destino; null = promo de inscripción por defecto, 0 = quitada.
	const [inscReingreso, setInscReingreso] = useState<ReentryInscriptionDTO | null>(null);
	const [insc, setInsc] = useState<number | null>(null);
	// Fase E: horarios sugeridos primero (el buscador completo sigue a un clic) y, si el destino vuelve a dar clase más tarde, el regreso.
	const [sugDatos, setSugDatos] = useState<SugerenciasDTO | null>(null);
	const [regreso, setRegreso] = useState<EleccionRegreso | null>(null);
	const [verBuscador, setVerBuscador] = useState<boolean>(false);
	// «Aplicar Ahora» nunca aplica de un clic: primero se confirma qué va a pasar.
	const [confirmando, setConfirmando] = useState<boolean>(false);

	const resetDecision = () => { setSugDatos(null); setRegreso(null); setVerBuscador(false); setInsc(null); setInscReingreso(null); setPromo(promoVacia()); setTypeId(null); setDestino(null); setBuscando(true); setToScheduleId(null); setToCourseId(null); setLastSession(""); setDueDayDraft(""); setNote(""); setCreatedId(null); };
	const resetAll = () => { resetDecision(); setOtrosPlanteles([]); setOptions(null); setEnrollmentId(null); setHistory(null); setCerradas([]); setStudentCode(""); setStudentId(null); setFound(null); setSearch(""); setAttendance(null); setDue(null); };

	// Búsqueda al escribir (con pausa), nunca por reloj.
	useEffect(() => {
		const texto = search.trim();
		if (texto.length < 2 || studentId) { setFound(null); return; }
		let vigente = true;
		const espera = setTimeout(() => {
			http.studentService.getStudentsByCampusId(campusId, 10, { search: texto })
				.then((r) => vigente && setFound((r.data?.data || []).map((s: any) => ({ id: s.id, name: `${s.firstName} ${s.lastName}`.trim(), code: s.identityCode || s.correlative || "", phone: s.phone1 || s.phone || "" }))))
				.catch((e) => vigente && notify(errorText(e), false));
		}, 300);
		return () => { vigente = false; clearTimeout(espera); };
	}, [search, campusId, studentId]);

	useEffect(() => {
		if (!studentId) { setHistory(null); setFalloHistorial(null); return; }
		let vigente = true;
		setFalloHistorial(null);
		http.enrollmentService.getStudentHistory(campusId, studentId).then((r) => {
			if (!vigente) { return; }
			const list = (r.data || []).filter((e) => e.isCurrentCampus && UPDATABLE_STATUSES.includes(e.statusId));
			setCerradas((r.data || []).filter((e) => e.isCurrentCampus && !UPDATABLE_STATUSES.includes(e.statusId) && !STATUS_SIN_AVISO.includes(e.statusId)));
			const fuera = Array.from(new Set((r.data || []).filter((e) => !e.isCurrentCampus && UPDATABLE_STATUSES.includes(e.statusId) && e.statusId !== 6).map((e) => e.campusId)));
			setOtrosPlanteles(fuera.map((id) => String(planteles.find((c: any) => c.id === id)?.name || "").replace(/^Washington Academy\s+/i, "")).filter(Boolean));
			setHistory(list);
			if (!enrollmentId || !list.some((e) => e.id === enrollmentId)) { setEnrollmentId(list.length === 1 ? list[0].id : null); }
		}).catch((e) => vigente && setFalloHistorial(errorText(e)));
		return () => { vigente = false; };
	}, [campusId, studentId, intento]);

	useEffect(() => {
		if (!enrollmentId) { setOptions(null); setAttendance(null); setFalloOpciones(null); return; }
		let vigente = true;
		setOptions(null); setAttendance(null); setFalloOpciones(null);
		http.enrollmentUpdatesService.options(campusId, enrollmentId).then((r) => {
			if (!vigente) { return; }
			setOptions(r.data);
			setSearch((s) => s || r.data.enrollment.studentName || "");
		}).catch((e) => vigente && setFalloOpciones(errorText(e)));
		// Solo su asistencia (antes se cargaba el horario completo para leer un porcentaje)
		apiBase.asistencia(campusId, enrollmentId).then((x) => vigente && setAttendance(x.data.attendancePct ?? null)).catch(() => { /* sin asistencia: la ficha se ve igual */ });
		return () => { vigente = false; };
	}, [campusId, enrollmentId, intento]);

	// Cambio de Vencimiento: pregunta quien lo hace directo (Supervisión, Contraloría) o lo pide a Aprobación (Caja del plantel).
	const pideVencimiento = cap.canDueDay || cap.isCaja || tieneCaja;
	useEffect(() => {
		if (!enrollmentId || !pideVencimiento) { setDue(null); return; }
		let vigente = true;
		http.enrollmentUpdatesService.getDueDay(campusId, enrollmentId).then((r) => vigente && setDue(r.data)).catch(() => { /* sin vencimiento: no sale el chip */ });
		return () => { vigente = false; };
	}, [campusId, enrollmentId, pideVencimiento, intento]);

	const isCourse = typeId === UPDATE_TYPE.COURSE_CHANGE;
	const isReentry = typeId === UPDATE_TYPE.REENTRY;
	useEffect(() => {
		setInsc(null); setInscReingreso(null);
		if (!isReentry || !toScheduleId || !enrollmentId) { return; }
		let vigente = true;
		http.enrollmentUpdatesService.reentryProjection(campusId, enrollmentId, toScheduleId)
			.then((r) => vigente && setInscReingreso(r.data.inscription || null))
			.catch((x) => vigente && notify(errorText(x), false));
		return () => { vigente = false; };
	}, [isReentry, toScheduleId, enrollmentId, campusId]);
	const isNextBlock = typeId === UPDATE_TYPE.NEXT_BLOCK;
	const destinos: TargetSchedule[] = useMemo(() => !options ? [] : isCourse ? options.courses.flatMap((c) => c.schedules)
		: isNextBlock ? (options.nextBlockSchedules || []) : options.targetSchedules, [options, isCourse, isNextBlock]);
	const cursoDe = (scheduleId: number | null) => options?.courses.find((c) => c.schedules.some((s) => s.id === scheduleId));
	const soloIds = useMemo(() => destinos.map((d) => d.id), [destinos]);
	const complemento = useMemo(() => destinos.map((t) => filaDeDestino(t, isCourse ? cursoCorto(options?.courses.find((c) => c.schedules.some((s) => s.id === t.id))?.name)
		: isNextBlock ? "Bloque " + ((t as any).blockName || "") : cursoCorto(options?.enrollment.courseName), campusId)), [destinos, options, isCourse, isNextBlock, campusId]);
	const detalle = useMemo(() => {
		const out: { [id: number]: DetalleHorario } = {};
		destinos.forEach((t) => { out[t.id] = { cupo: t.limit || null, alumnos: t.enrolled, lleno: t.isFull, sesion: t.classesGiven + 1 }; });
		return out;
	}, [destinos]);
	const target: TargetSchedule | undefined = destinos.find((t) => t.id === toScheduleId);
	const conSugerencias = typeId === UPDATE_TYPE.SCHEDULE_CHANGE || typeId === UPDATE_TYPE.REENTRY;
	// El destino elegido (de las sugerencias o del buscador) con su próxima clase: si cae a más de una semana, se elige el regreso.
	const destinoSug: SugerenciaHorario | undefined = conSugerencias && toScheduleId ? (sugDatos?.suggestions || []).find((x) => x.id === toScheduleId) : undefined;
	const pideRegreso = !!destinoSug?.needsReturnPlan;
	const elegirDestino = (id: number) => { setToScheduleId(id); setRegreso(null); setBuscando(false); };

	const e = options?.enrollment;
	const tipos = typeChips(options?.types);
	const bloqueo = (id: number): string | null => { const t = tipos.find((x) => x.id === id); return t && !t.allowed ? (t.reason || TYPE_LABELS[id]) : null; };
	// Mismos estatus que acepta el servidor (EnrollmentDueDayService::set): activa, por comenzar y flotantes (Aplazado, Sin Horario, Pausado).
	const venceEditable = [1, 4, 7, 9, 10].includes(e?.statusId ?? -1);
	const chips: number[] = [...tipos.map((t) => t.id), ...(due && (due.canEdit || cap.isCaja || tieneCaja) && venceEditable ? [CHIP_VENCIMIENTO] : [])];
	const pendingAlready = !!options?.pendingUpdateId;
	const taken = e?.sessionsTaken ?? 0;
	const delta = target && !isCourse ? sessionDelta(taken, target.classesGiven) : { less: 0, more: 0 };
	const destOk = !needsSchedule(typeId) || (!!toScheduleId && (!isCourse || (!!toCourseId && !faltaPromo(promo, [toScheduleId]))) && (!pideRegreso || regresoListo(regreso)));
	const noteOk = note.trim().length >= 3;
	const dayNum = Number(dueDayDraft);
	const dueOk = typeId === CHIP_VENCIMIENTO && Number.isInteger(dayNum) && dayNum >= 1 && dayNum <= 28;
	const canSend = !!e && typeId !== null && typeId !== CHIP_VENCIMIENTO && destOk && noteOk && !busy && !pendingAlready && !createdId;
	const enviar = regresoParaEnviar(pideRegreso ? regreso : null, typeId || 0, toScheduleId || 0);
	// Pausar Hasta no mueve hoy a nadie: las sesiones de más del destino no aplican (las revisa el reingreso del día del regreso)
	// Fase 2: cambio a un grupo cuyo calendario de pagos no coincide → Recorrer Pagos (marcado si se atrasa, sin marcar si va adelante).
	const esCambioHorario = typeId === UPDATE_TYPE.SCHEDULE_CHANGE && !!toScheduleId && !pideRegreso && !!e;
	const { vista: vistaSug } = useVistaRecorrer(campusId, e?.id, null, toScheduleId, esCambioHorario);
	const sug = esCambioHorario ? (vistaSug?.sugerido ?? 0) : 0;
	const [rec, setRec] = useState<EleccionRecorrer>({ on: false, meses: 0 });
	const [vistaRec, setVistaRec] = useState<RecorrerVistaDTO | null>(null);
	useEffect(() => { setRec({ on: sug > 0, meses: sug }); setVistaRec(null); }, [sug, toScheduleId]);
	const recorreRepetidas = rec.on && sug > 0;
	// Pausar Hasta no mueve hoy a nadie; con pagos recorridos las clases que repite no se cobran aparte (Caja aplica directo).
	const porCaja = cap.isCaja || (cap.isAcademic && ACADEMIC_APPLY_TYPES.includes(enviar.typeId) && sug === 0);
	const sugListo = !esCambioHorario || !!vistaSug;
	const canNow = canSend && sugListo && canApplyNow && porCaja && (delta.more === 0 || regreso?.tipo === "pausa" || (recorreRepetidas && cap.isCaja));
	// Por qué no sale «Aplicar Ahora» (revisión 1-nov: se escondía sin decir nada)
	const sinAhora = canSend && canApplyNow && !canNow ? (delta.more > 0 && !recorreRepetidas ? "Repite clases: lo revisa Caja." : "Mueve dinero: lo aplica Caja.") : null;

	const send = (applyNow: boolean) => {
		if (!e || typeId === null) { return; }
		setBusy(true);
		http.enrollmentUpdatesService.create(campusId, { applyNow: applyNow && !isCourse, items: [{
			enrollmentId: e.id, typeId: enviar.typeId, note: note.trim(), options: enviar.options,
			toScheduleId: needsSchedule(typeId) ? toScheduleId : null, toCourseId: isCourse ? toCourseId : null,
			promotion: isCourse && toScheduleId ? promoParaEnviar(promo, toScheduleId, {}, true) : isReentry && toScheduleId ? promoDeReingreso(toScheduleId, insc) : undefined,
			lastSessionTaken: typeId === UPDATE_TYPE.RETIRED && lastSession ? Number(lastSession) : null,
			recorrer: sug !== 0 ? (rec.on ? rec.meses : 0) : undefined,
			extraSessionsOption: recorreRepetidas && delta.more > 0 ? 4 : undefined
		} as any] })
			.then((r) => {
				changed();
				// Cambio a otro curso: el reparto se arma sobre la actualización ya creada.
				if (isCourse && applyNow && r.data.updates?.[0]) { setCreatedId(r.data.updates[0].id); notify(S.sent(1)); return; }
				// Lo que dice el servidor manda: sin permiso de aplicar (o si toca dinero) la actualización queda Por Aplicar.
				const quedoPendiente = (r.data.updates || []).some((u) => u.statusId === 1);
				notify(applyNow && !quedoPendiente ? S.applied : S.sent(1));
				resetAll();
			})
			.catch((x) => notify(errorText(x), false))
			.finally(() => setBusy(false));
	};

	// Lo que el «Aplicar Ahora» va a hacer, armado con lo que la pantalla ya tiene (mismo modal que Por Aplicar).
	const destinoHorario = destino && destino.id === toScheduleId ? destino.horario : nombreHorario(destinoSug?.name || target?.name);
	const destinoPeriodo = (destino && destino.id === toScheduleId ? destino.periodo : destinoSug?.periodName || target?.periodName) || "";
	const destinoTexto = toScheduleId ? [destinoHorario, destinoPeriodo].filter(Boolean).join(", ") : "";
	const personaConfirmar = (): PersonaConfirmar | null => {
		if (!e || typeId === null) { return null; }
		const t = enviar.typeId;
		const tipo = tipoDe({ typeId: t, fromSchedule: { periodName: e.periodName }, toSchedule: { periodName: destinoPeriodo } });
		const lineas: string[] = [];
		if (S.estadoQueda[t]) { lineas.push(S.quedaEstado(S.estadoQueda[t])); }
		if (destinoTexto) { lineas.push(t === UPDATE_TYPE.PAUSED ? S.regresaA(destinoTexto) : S.pasaA(destinoTexto)); }
		if (t !== UPDATE_TYPE.PAUSED && delta.more > 0) { lineas.push(S.repiteClases(delta.more)); }
		if (t !== UPDATE_TYPE.PAUSED && delta.less > 0) { lineas.push(S.faltanClases(delta.less)); }
		if (e.overdue > 0) { lineas.push(S.debeVencidos(dinero(e.overdue, countryId))); }
		if (sug !== 0) { if (rec.on && vistaRec) { lineas.push(...vistaRec.lineas); } else if (!rec.on) { lineas.push(S.mismosMeses); } }
		else if (tipo === "Cambio de Periodo") { lineas.push(S.mismosMeses); }
		return { nombre: e.studentName, curso: cursoCorto(e.courseName) || e.courseName, tipo, lineas };
	};
	const persona = confirmando ? personaConfirmar() : null;

	const saveDue = () => {
		if (!e || !dueOk || !noteOk) { return; }
		if (!due?.canEdit) {
			// Caja: la misma ventana «Enviar a Aprobación» de la ficha; la aprueba Supervisión de Caja del plantel.
			aprobacion.pedir({ kind: "cambiar_vencimiento", titulo: "Cambio de Vencimiento", motivoInicial: note.trim(),
				data: { enrollmentId: e.id, dueDay: dayNum, note: note.trim() },
				onDone: (r) => { notify(r.text); setNote(""); setDueDayDraft(""); setTypeId(null); setIntento((n) => n + 1); changed(); } });
			return;
		}
		setBusy(true);
		http.enrollmentUpdatesService.setDueDay(campusId, { enrollmentId: e.id, dueDay: dayNum, note: note.trim() })
			// Se recarga la matrícula: el plan decía el día viejo («vence el 5») hasta volver a abrirla.
			.then((r) => { setDue(r.data); notify(S.dueDaySaved); setNote(""); setDueDayDraft(""); setTypeId(null); setIntento((n) => n + 1); })
			.catch((x) => notify(errorText(x), false))
			.finally(() => setBusy(false));
	};

	const plan = e ? planLinea(e.tuitions) : null;
	const sesionesTotal = e?.sessionsTotal || 0;

	return (
		<div className="wau-caja wau-pila">
			<div className="wau-buscar">
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
				<input value={search} placeholder={S.searchPlaceholder} aria-label={S.searchStudent} autoFocus={!initialStudentId}
					onChange={(ev) => { if (studentId) { resetAll(); } setSearch(ev.target.value); }} />
				{studentId ? <button type="button" className="wau-btn s mini" onClick={resetAll}>{S.buscarOtro}</button> : null}
			</div>
			{found && !studentId ? (
				<div className="wau-resultados">
					{found.length === 0 ? <span className="wau-sec" style={{ padding: 10 }}>{S.empty}</span> : found.map((s) => (
						<button key={"st" + s.id} type="button" onClick={() => { setStudentId(s.id); setStudentCode(s.code); setEnrollmentId(null); setOptions(null); resetDecision(); setSearch(s.name); setFound(null); }}>
							<b>{s.name}</b><span className="wau-sec">{[s.code, s.phone].filter(Boolean).join(", ")}</span>
						</button>
					))}
				</div>
			) : null}
			{history && history.length > 1 ? (
				<div className="wau-chips" role="group">
					{history.map((h) => (
						<button key={"en" + h.id} type="button" className="wau-chip" aria-pressed={enrollmentId === h.id} onClick={() => { setEnrollmentId(h.id); resetDecision(); }}>
							{cursoCorto(h.courseName) || h.courseName}, {etiquetaHorario(h.scheduleName, h.periodName) || "—"}, {enrollmentStatusLabel(h.statusId, h.status)}
						</button>
					))}
				</div>
			) : history && history.length === 0 ? (
				<div className="wau-sin-matricula">
					<p><b>{S.sinMatriculaVigente(search.trim())}</b></p>
					{otrosPlanteles.length > 0 ? <p className="wau-oro">{`Tiene matrícula vigente en ${otrosPlanteles.join(" y ")}: cambia el plantel de arriba para actualizarla.`}</p> : null}
					{cerradas.length > 0 ? (
						<ul>
							{cerradas.map((h) => (
								<li key={"ce" + h.id}>{cursoCorto(h.courseName) || h.courseName}{nombreHorario(h.scheduleName) ? ", " + etiquetaHorario(h.scheduleName, h.periodName) : ""}: <b>{enrollmentStatusLabel(h.statusId, h.status)}</b></li>
							))}
						</ul>
					) : null}
					<p className="wau-sec">{S.matricularPorSolicitud}</p>
					{puedeSolicitar && studentCode ? (
						<Link to={"/enrollmentRequests?matricular=" + encodeURIComponent(studentCode)} className="wau-btn p mini" style={{ alignSelf: "flex-start" }}>{S.hacerSolicitud}</Link>
					) : null}
				</div>
			) : null}

			{falloHistorial ? <Fallo dentro texto={falloHistorial} onReintentar={() => setIntento((n) => n + 1)} /> : null}
			{falloOpciones ? <Fallo dentro texto={falloOpciones} onReintentar={() => setIntento((n) => n + 1)} /> : enrollmentId && !options && !falloHistorial ? <div className="wau-sec">{S.loading}</div> : null}
			{e && options ? (
				<div className="wau-dos">
					<div className="wau-ficha">
						<div className="wau-ficha-cab"><h3>{e.studentName}</h3><span className={"wau-pill " + pillEstatus(e.statusId)}>{enrollmentStatusLabel(e.statusId, e.statusLabel)}</span></div>
						<p className="wau-sec">{[e.identityCode, e.phone].filter(Boolean).join(", ")}</p>
						<dl className="wau-kv">
							<dt>{S.enrollment}</dt><dd>{cursoCorto(e.courseName) || e.courseName}{e.levelName ? ", " + e.levelName : ""}</dd>
							<dt>{S.schedule}</dt><dd>{etiquetaHorario(e.scheduleName, e.periodName) || "—"}{e.mentorName ? ", " + e.mentorName : ""}</dd>
							{plan ? <><dt>{S.planLabel}</dt><dd>{S.planLine(plan.n, plan.m, plan.dia)}</dd></> : null}
							<dt>{S.progressLabel}</dt><dd>{S.sessionsLine(e.sessionsTaken, sesionesTotal, attendance)}{e.sessionsToRecover > 0 ? <span className="wau-oro">, {S.lessSessions(e.sessionsToRecover)}</span> : null}</dd>
							<dt>{T.pendiente}</dt><dd className="wau-num">{dinero(e.debt, countryId)}</dd>
							<dt>{T.vencido}</dt><dd className={"wau-num" + (e.overdue > 0 ? " wau-oro" : "")}>{e.overdue > 0 ? dinero(e.overdue, countryId) : T.alCorriente}</dd>
							<dt>{T.pagado}</dt><dd className="wau-num">{dinero(e.paid, countryId)}</dd>
							<dt>{T.pagadoHasta}</dt><dd>{e.paidThrough ? e.paidThrough.replace(/^Mensualidad\s+/i, "") : T.nada}</dd>
							{e.paidNotCoursed > 0 ? <><dt>{S.paidNotCoursed}</dt><dd className="wau-oro">{dinero(e.paidNotCoursed, countryId)}</dd></> : null}
						</dl>
						<RegresoProgramado key={"rp" + e.id + ":" + intento} campusId={campusId} enrollmentId={e.id} puedeCancelar={cap.canApply && (cap.isCaja || cap.isAcademic)} notify={notify} onCambio={() => { changed(); setIntento((n) => n + 1); }} />
						<div style={{ marginTop: 10 }}><button type="button" className="wau-btn s mini" onClick={() => setVerCuenta(true)}>{T.estadoCuenta}</button></div>
					</div>

					<div className="wau-ficha wau-pila">
						<h3>{S.quienCambia}</h3>
						<div className="wau-chips" role="group">
							{chips.map((id) => (
								<button key={"ty" + id} type="button" className="wau-chip" aria-pressed={typeId === id} disabled={!!createdId}
									aria-disabled={!!bloqueo(id)} title={bloqueo(id) || undefined} style={bloqueo(id) ? { opacity: .5 } : undefined}
									onClick={() => { const no = bloqueo(id); if (no) { notify(no, false); return; } setTypeId(id); setDestino(null); setBuscando(true); setToScheduleId(null); setToCourseId(null); setSugDatos(null); setRegreso(null); setVerBuscador(false); }}>
									{id === CHIP_VENCIMIENTO ? "Cambio de Vencimiento" : TYPE_LABELS[id]}
								</button>
							))}
						</div>
						{pendingAlready ? <p className="wau-adv">{S.alreadyPending}</p> : null}

						{needsSchedule(typeId) ? (
							<div className="wau-pila">
								<span className="wau-lab">{isCourse ? S.cursoDestino : S.horarioDestino}</span>
								{conSugerencias && !verBuscador && !createdId && e ? (
									<>
										<SugerenciasHorario key={"sg" + e.id + ":" + typeId} campusId={campusId} enrollmentId={e.id} mode={isReentry ? "reentry" : "change"} seleccionado={toScheduleId}
											onDatos={setSugDatos} onElegir={(x) => { setDestino(null); elegirDestino(x.id); }} />
										<button type="button" className="wau-btn s mini" style={{ alignSelf: "flex-start" }} onClick={() => setVerBuscador(true)}>{S.buscarOtroHorario}</button>
									</>
								) : destino && !buscando ? (
									<div className="wau-destino">
										<span><b>{destino.horario}</b>{destino.periodo ? ", " + destino.periodo : ""}{destino.modulo ? ", " + destino.modulo : ""}{destino.maestro ? ", " + destino.maestro : ""}</span>
										<button type="button" className="wau-btn s mini" disabled={!!createdId} onClick={() => setBuscando(true)}>{S.cambiar}</button>
									</div>
								) : (
									<BuscadorHorarios key={"b" + typeId} compacto campusId={campusId} soloIds={soloIds} complemento={complemento} detalle={detalle} seleccionado={toScheduleId}
										onElegir={(h) => { setDestino(h); setToScheduleId(h.id); setRegreso(null); setToCourseId(isCourse ? cursoDe(h.id)?.id ?? null : null); setBuscando(false); }} />
								)}
								{conSugerencias && verBuscador && !createdId ? <button type="button" className="wau-btn s mini" style={{ alignSelf: "flex-start" }} onClick={() => { setVerBuscador(false); setBuscando(true); }}>{S.verSugerencias}</button> : null}
								{destinoSug && pideRegreso && !createdId ? <RegresoClasesOpciones destino={destinoSug} datos={sugDatos} valor={regreso} onCambio={setRegreso} deshabilitado={busy} /> : null}
							</div>
						) : null}

						{typeId === UPDATE_TYPE.RETIRED ? (
							<label className="wau-campo" style={{ maxWidth: 220 }}><span className="wau-lab">{S.lastSession}</span>
								<input className="wau-entrada wau-num" inputMode="numeric" value={lastSession} onChange={(ev) => setLastSession(ev.target.value.replace(/\D/g, ""))} /></label>
						) : null}
						{typeId === CHIP_VENCIMIENTO ? (
							<label className="wau-campo" style={{ maxWidth: 220 }}><span className="wau-lab">{S.dueDay}{due?.effectiveDueDay ? `, hoy ${due.effectiveDueDay}` : ""}</span>
								<input className="wau-entrada wau-num" inputMode="numeric" maxLength={2} value={dueDayDraft} onChange={(ev) => setDueDayDraft(ev.target.value.replace(/\D/g, ""))} /></label>
						) : null}

						{isReentry && toScheduleId && !createdId ? <InscripcionReingreso info={inscReingreso} value={insc} onChange={setInsc} busy={busy} /> : null}
						{isCourse && toScheduleId && !createdId && e ? (
							<PromocionDirecta campusId={campusId} studentId={e.studentId} studentName={e.studentName} scheduleIds={[toScheduleId]} value={promo} onChange={setPromo} busy={busy} marcar={false} />
						) : null}

						{typeId !== null && typeId !== CHIP_VENCIMIENTO && (target || e.overdue > 0) ? (
							<div className="wau-nota">
								{target ? <span>{S.goesInSession(target.classesGiven + 1)}{delta.more > 0 ? ", " + S.repiteClasesMin(delta.more) : ""}{delta.less > 0 ? <b className="wau-oro">, {S.lessSessions(delta.less)}</b> : null}</span> : null}
								{e.overdue > 0 ? <span className="wau-oro">{S.overdueOf(dinero(e.overdue, countryId))}</span> : null}
							</div>
						) : null}

						{sug !== 0 && e && !createdId ? (
							<RecorrerEleccion campusId={campusId} enrollmentId={e.id} toScheduleId={toScheduleId} sugerido={sug} valor={rec} onCambio={setRec} onVista={setVistaRec} disabled={busy} />
						) : null}

						{typeId !== null && !createdId ? (
							<>
								<textarea className="wau-entrada" rows={2} placeholder={S.notePlaceholder} aria-label={S.note} value={note} onChange={(ev) => setNote(ev.target.value)} />
								<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
									{typeId === CHIP_VENCIMIENTO ? (
										<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !dueOk || !noteOk} onClick={saveDue}>{due?.canEdit ? S.saveDueDay : ((S as any).enviarAprobacion || "Enviar a Aprobación")}</button>
									) : (
										<>
											{canNow ? <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={() => isCourse ? send(true) : setConfirmando(true)}>{S.applyNow}</button> : null}
											<button type="button" className={canNow ? "btn btn-outline-secondary btn-sm" : "btn btn-primary btn-sm rounded-pill"} disabled={!canSend} onClick={() => send(false)}>{S.leavePending}</button>
											{sinAhora ? <span className="wau-sec small">{sinAhora}</span> : null}
										</>
									)}
								</div>
							</>
						) : null}
					</div>
				</div>
			) : null}

			{persona ? <ConfirmarAplicar personas={[persona]} busy={busy} onCancel={() => setConfirmando(false)} onConfirm={() => { setConfirmando(false); send(true); }} /> : null}
			{aprobacion.modal}
			{verCuenta && e ? <EstadoCuentaModal campusId={campusId} studentId={e.studentId} studentName={e.studentName} enrollmentId={e.id} onClose={() => setVerCuenta(false)} /> : null}
			{createdId ? (
				<div className="wau-pila">
					<UpdateDetail campusId={campusId} countryId={countryId} id={createdId} notifyError={(m) => notify(m, false)}
						onChanged={(m) => { notify(m); changed(); resetAll(); }} />
					<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
						<button type="button" className="wau-btn s" onClick={() => resetAll()}>{S.leavePending}</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default UnaAlumna;
